import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { MileagePlanProfileLookupResponse } from '../../../dtos/response/mileage-plan-profile-lookup-response/mileage-plan-profile-lookup-response';
import { MileagePlanProfileLookupStatus } from '../../../dtos/response/mileage-plan-profile-lookup-response/mileage-plan-profile-lookup-status';
import { MileagePlanProfileSearchResponse } from '../../../dtos/response/mileage-plan-profile-search-response/mileage-plan-profile-search-response';
import { MileagePlanProfileSearchStatus } from '../../../dtos/response/mileage-plan-profile-search-response/mileage-plan-profile-search-status';
import { AtmosGuestSearchUseCase } from '../../../shared/use-cases/atmos-guest-search.use-case';
import {
  getMileagePlanProfileSearchAirlineCode,
  getMileagePlanProfileSearchMileagePlanNumber,
} from '../../../state/mileage-plan-profile-search/mileage-plan-profile-search.selectors';
import { RootState } from '../../../state/state';
import { MileagePlanMessageConverter } from '../../../utils/message-converters/mileage-plan/mileage-plan-message-converter';
import { resetJetStreamMessagesForNonFiltered } from '../../message-service/state/message.actions';
import { MileagePlanProfileService } from '../mileage-plan-profile.service';
import {
  mileagePlanProfileSearch,
  mileagePlanProfileSearchByNameAndDob,
  mileagePlanProfileSearchByNameAndDobFailure,
  mileagePlanProfileSearchByNameAndDobSuccess,
  mileagePlanProfileSearchByPhoneNumber,
  mileagePlanProfileSearchByPhoneNumberFailure,
  mileagePlanProfileSearchByPhoneNumberSuccess,
  mileagePlanProfileSearchFailure,
  mileagePlanProfileSearchSuccess,
  resetSimpleMileagePlanProfileLookupState,
  simpleMileagePlanProfileSearch,
  simpleMileagePlanProfileSearchComplete,
} from './mileage-plan-profile-service.actions';

@Injectable()
export class MileagePlanProfileServiceEffects {
  private readonly actions$ = inject(Actions);
  private readonly mileagePlanProfileService = inject(MileagePlanProfileService);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly mileagePlanMessageConverter = inject(MileagePlanMessageConverter);
  private readonly atmosGuestSearchUseCase = inject(AtmosGuestSearchUseCase);

  /**
   * Effect to handle mileage plan profile search for the full Mileage Plan Profile feature
   */
  mileagePlanProfileSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(mileagePlanProfileSearch),
      debounceTime(300),
      withLatestFrom(
        this.store.select(getMileagePlanProfileSearchMileagePlanNumber),
        this.store.select(getMileagePlanProfileSearchAirlineCode)
      ),
      mergeMap(([action, mileagePlanNumber, airlineCode]) =>
        this.mileagePlanProfileService.mileagePlanProfileLookup(mileagePlanNumber, airlineCode).pipe(
          map((result) => {
            this.store.dispatch(resetJetStreamMessagesForNonFiltered());
            result.mappedMessageKeys = this.mileagePlanMessageConverter.convertMileagePlanLookupMessage(result);
            return result.status === MileagePlanProfileLookupStatus.SUCCESS ||
              result.status === MileagePlanProfileLookupStatus.PARTIAL_FAILURE
              ? mileagePlanProfileSearchSuccess(result)
              : mileagePlanProfileSearchFailure(result);
          })
        )
      ),
      catchError(() => {
        const response: MileagePlanProfileLookupResponse = { status: MileagePlanProfileLookupStatus.UNCAUGHT, isProfileLookup: true };
        response.mappedMessageKeys = this.mileagePlanMessageConverter.convertMileagePlanLookupMessage(response);
        return of(mileagePlanProfileSearchFailure(response));
      })
    )
  );

  /**
   * Effect to handle simple mileage plan profile searches, for example: mileage plan account checks within other features
   */
  simpleMileagePlanProfileSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(simpleMileagePlanProfileSearch),
      mergeMap((action) =>
        this.mileagePlanProfileService.mileagePlanProfileLookup(action.mileagePlanNumber, action.airlineCode, action.backgroundLookup).pipe(
          map((result) => {
            result.featureId = action.featureId;
            result.mappedMessageKeys = this.mileagePlanMessageConverter.convertMileagePlanLookupMessage(result);
            result.backgroundLookup = action.backgroundLookup;
            return simpleMileagePlanProfileSearchComplete(result);
          }),
          catchError(() => {
            // Capture action.featureId from outer scope
            const response: MileagePlanProfileLookupResponse = {
              featureId: action.featureId,
              status: MileagePlanProfileLookupStatus.UNCAUGHT,
            };
            response.mappedMessageKeys = this.mileagePlanMessageConverter.convertMileagePlanLookupMessage(response);
            return of(simpleMileagePlanProfileSearchComplete(response));
          })
        )
      )
    )
  );

  resetSimpleMileagePlanProfileLookupState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resetSimpleMileagePlanProfileLookupState),
        tap(() => this.atmosGuestSearchUseCase.clear())
      ),
    { dispatch: false }
  );

  mileagePlanProfileSearchByPhoneNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(mileagePlanProfileSearchByPhoneNumber),
      debounceTime(300),
      switchMap((action) =>
        this.mileagePlanProfileService.mileagePlanProfileSearchByPhoneNumber(action.searchByPhoneNumberRequest).pipe(
          map((result) => {
            // Handle error messages here
            result.mappedMessageKeys = this.mileagePlanMessageConverter.convertMileagePlanLookupMessage(result);
            return result.status === MileagePlanProfileSearchStatus.SUCCESS
              ? mileagePlanProfileSearchByPhoneNumberSuccess(result)
              : mileagePlanProfileSearchByPhoneNumberFailure(result);
          })
        )
      ),
      catchError(() => {
        const response: MileagePlanProfileSearchResponse = { status: MileagePlanProfileSearchStatus.UNCAUGHT, isProfileSearch: true };
        // Handle error messages here
        response.mappedMessageKeys = this.mileagePlanMessageConverter.convertMileagePlanLookupMessage(response);
        return of(mileagePlanProfileSearchByPhoneNumberFailure(response));
      })
    )
  );

  mileagePlanProfileSearchByNameAndDob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(mileagePlanProfileSearchByNameAndDob),
      debounceTime(300),
      switchMap((action) =>
        this.mileagePlanProfileService.mileagePlanProfileSearchByNameAndDob(action.searchByNameAndDobRequest).pipe(
          map((result) => {
            // Handle error messages here
            result.mappedMessageKeys = this.mileagePlanMessageConverter.convertMileagePlanLookupMessage(result);
            return result.status === MileagePlanProfileSearchStatus.SUCCESS
              ? mileagePlanProfileSearchByNameAndDobSuccess(result)
              : mileagePlanProfileSearchByNameAndDobFailure(result);
          })
        )
      ),
      catchError(() => {
        const response: MileagePlanProfileSearchResponse = { status: MileagePlanProfileSearchStatus.UNCAUGHT, isProfileSearch: true };
        // Handle error messages here
        response.mappedMessageKeys = this.mileagePlanMessageConverter.convertMileagePlanLookupMessage(response);
        return of(mileagePlanProfileSearchByNameAndDobFailure(response));
      })
    )
  );
}
